import React, {Fragment} from "react";
import PropTypes from "prop-types";
import moment from "moment";
import Page from "../../../../../components/BaseComponents/Page/Page";
import {FaSolidIcon} from "../../../../../components/FontAwesomeIcons";
import {InputDatepicker} from "../../../../../components/BaseComponents/InputDatepicker";
import {ModuloHRAPI} from "../../../../../api/ModuloHR/ModuloHRAPI";
import {swal, swalError, swalLoading, swalSuccess} from "../../../../../helpers/SweetAlertWrapper";

const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default class LeaveRequestsNewRequestPage extends React.Component {
    constructor(props) {
        super(props);
        const defaultStaffId = props.selectedStaffId || this.getDefaultStaffId(props.staffList);
        this.state = {
            id_dipendente: defaultStaffId,
            data_inizio: "",
            data_inizio_display: "",
            data_fine: "",
            data_fine_display: "",
            reason: "",
            attachments: []
        };
    }

    getDefaultStaffId = staffList => {
        if (!staffList || !staffList.length) return null;
        return staffList[0].id_dipendente || null;
    }

    handleStaffChange = e => {
        const id_dipendente = parseInt(e.target.value, 10) || null;
        this.setState({id_dipendente: id_dipendente});
    }

    handleStartDateChange = e => {
        const selectedDate = e.value;
        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
        const displayDate = moment(selectedDate).format("DD-MM-YYYY");
        this.setState({data_inizio: formattedDate, data_inizio_display: displayDate});
    }

    handleEndDateChange = e => {
        const selectedDate = e.value;
        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
        const displayDate = moment(selectedDate).format("DD-MM-YYYY");
        this.setState({data_fine: formattedDate, data_fine_display: displayDate});
    }

    handleReasonChange = e => this.setState({reason: e.target.value});

    handleAttachmentsChange = e => {
        const files = Array.from(e.target.files || []);
        if (files.length > MAX_ATTACHMENTS) {
            swalError("Puoi caricare al massimo 3 allegati");
            return;
        }
        const oversizedFile = files.find(file => file.size > MAX_FILE_SIZE);
        if (oversizedFile) {
            swalError("Ogni allegato deve essere massimo di 5MB");
            return;
        }
        this.setState({attachments: files});
    }

    handleRemoveAttachment = index => _ => {
        const attachments = this.state.attachments.filter((_, i) => i !== index);
        this.setState({attachments: attachments});
    }

    validateForm = () => {
        if (!this.state.id_dipendente) {
            swalError("Specifica un collaboratore valido");
            return false;
        }
        if (!this.state.data_inizio || !this.state.data_fine) {
            swalError("Specifica una data di inizio e fine valide");
            return false;
        }
        const startDate = moment(this.state.data_inizio, "YYYY-MM-DD", true);
        const endDate = moment(this.state.data_fine, "YYYY-MM-DD", true);
        if (!startDate.isValid() || !endDate.isValid()) {
            swalError("Specifica una data di inizio e fine valide");
            return false;
        }
        if (endDate.isBefore(startDate)) {
            swalError("La data di fine deve essere uguale o successiva alla data di inizio");
            return false;
        }
        if (!this.state.reason || !this.state.reason.trim()) {
            swalError("Specifica una motivazione valida");
            return false;
        }
        if (this.state.reason.length > 255) {
            swalError("La motivazione deve essere massimo 255 caratteri");
            return false;
        }
        return true;
    }

    handleSubmit = _ => {
        if (!this.validateForm()) return;
        swalLoading();
        const payload = {
            id_dipendente: this.state.id_dipendente,
            data_inizio: this.state.data_inizio,
            data_fine: this.state.data_fine,
            reason: this.state.reason
        };
        ModuloHRAPI.LeaveRequests.save(payload, this.state.attachments)
            .then(_ => {
                swal.safeClose();
                swalSuccess("Richiesta inviata");
                if (this.props.onRequestCreated) {
                    this.props.onRequestCreated(this.state.id_dipendente);
                }
            })
            .catch(error => {
                swal.safeClose();
                swalError(error || "Non e' stato possibile inviare la richiesta");
            });
    }

    render() {
        return (
            <Fragment>
                <Page
                    title={"Nuova richiesta"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={this.props.active}
                    content={
                        <div className={"container py-3"}>
                            <div className={"form-group"}>
                                <label>Collaboratore</label>
                                <select
                                    className={"form-control"}
                                    value={this.state.id_dipendente || ""}
                                    onChange={this.handleStaffChange}
                                >
                                    {
                                        (this.props.staffList || []).map(staff => {
                                            const label = staff.negozio?.nome ? `${staff.nome} - ${staff.negozio.nome}` : staff.nome;
                                            return (
                                                <option key={staff.id_dipendente} value={staff.id_dipendente}>{label}</option>
                                            );
                                        })
                                    }
                                </select>
                            </div>

                            <div className={"form-row"}>
                                <div className={"form-group col-6"}>
                                    <label>Data inizio</label>
                                    <InputDatepicker
                                        className={"form-control bg-main-z3"}
                                        value={this.state.data_inizio_display}
                                        onChange={this.handleStartDateChange}
                                    />
                                </div>
                                <div className={"form-group col-6"}>
                                    <label>Data fine</label>
                                    <InputDatepicker
                                        className={"form-control bg-main-z3"}
                                        value={this.state.data_fine_display}
                                        onChange={this.handleEndDateChange}
                                    />
                                </div>
                            </div>

                            <div className={"form-group"}>
                                <label>Motivazione</label>
                                <textarea
                                    className={"form-control"}
                                    rows={3}
                                    value={this.state.reason}
                                    onChange={this.handleReasonChange}
                                />
                            </div>

                            <div className={"form-group"}>
                                <label>Allegati (max 3)</label>
                                <input
                                    type={"file"}
                                    multiple={true}
                                    className={"form-control-file"}
                                    onChange={this.handleAttachmentsChange}
                                />
                                {
                                    this.state.attachments.length > 0 &&
                                    <ul className={"list-group mt-2"}>
                                        {
                                            this.state.attachments.map((file, index) => {
                                                return (
                                                    <li key={index} className={"list-group-item d-flex justify-content-between align-items-center"}>
                                                        <span className={"small"}>{file.name}</span>
                                                        <button
                                                            type={"button"}
                                                            className={"btn btn-sm btn-outline-secondary"}
                                                            onClick={this.handleRemoveAttachment(index)}
                                                        >
                                                            Rimuovi
                                                        </button>
                                                    </li>
                                                );
                                            })
                                        }
                                    </ul>
                                }
                            </div>

                            <div className={"text-right"}>
                                <button
                                    type={"button"}
                                    className={"btn btn-primary"}
                                    onClick={this.handleSubmit}
                                >
                                    Invia richiesta
                                </button>
                            </div>
                        </div>
                    }
                />
            </Fragment>
        );
    }
}

LeaveRequestsNewRequestPage.propTypes = {
    active: PropTypes.bool,
    onDismiss: PropTypes.func,
    onRequestCreated: PropTypes.func,
    staffList: PropTypes.array,
    selectedStaffId: PropTypes.number
};
